import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ChevronDown } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, StatusPill } from '@lyearn/ui';
import SingleSelectFilter from '@/components/FilterManager/SingleSelectFilter';
import { SortMenuProps } from '@/components/SortMenu/types';
import { FilterOperatorEnum } from '@/types/schema';

import { OperatorSelectProps, OperatorSelectVariant } from './type';
import { getOperatorSelectValues } from './utils';

const CLASS_OVERRIDES: SortMenuProps['overrides'] = {
  PopoverContent: { maxWidth: 13.6 },
};

function OperatorSelect(props: OperatorSelectProps) {
  const { t } = useTranslation();
  const {
    variant = OperatorSelectVariant.AndOr,
    selectedValue,
    onChange,
    className,
    disabled,
  } = props;
  const values = useMemo(() => getOperatorSelectValues(t, variant), [t, variant]);
  const isAndSelected = selectedValue === FilterOperatorEnum.And;
  const title = useMemo(
    () => values.find((item) => item.id === selectedValue),
    [selectedValue, values],
  );
  const _onChange = useCallback((val: string) => onChange(val as FilterOperatorEnum), [onChange]);

  return (
    <SingleSelectFilter
      disabled={disabled}
      items={values}
      overrides={CLASS_OVERRIDES}
      selectedItems={[selectedValue]}
      onChange={_onChange}>
      {({ isOpen }: { isOpen: boolean }) => (
        <StatusPill
          appearance={isAndSelected ? 'purple' : 'warning'}
          className={cn(
            'label-caps cursor-pointer border border-solid border-white pe-4 ps-8',
            className,
            isAndSelected
              ? {
                  'border-text-purple': isOpen,
                }
              : {
                  'border-text-warning': isOpen,
                },
          )}
          label={
            <Stack alignItems="center">
              {title?.label.toUpperCase()} <ChevronDown className="h-16 w-16 flex-shrink-0" />
            </Stack>
          }
          variant="light"
        />
      )}
    </SingleSelectFilter>
  );
}

export default OperatorSelect;
